import { get, post } from '../api.js';
import { deriveAndStoreKeys, hasKeys, clearKeys, migrateToMasterKey, rewrapItemKeys, applyMigratedKeys, needsParamUpgrade, needsAadUpgrade, upgradeArgonParams, unwrapAndStoreKeysFromPrf, getEncKey, getAuthKeyHex, wipe } from '../crypto.js';
import { isTauri } from '../backend.js';
import { navigate } from '../router.js';
import { isWebAuthnAvailable, authenticate } from '../webauthn.js';
import { lucideIcons, esc } from '../app.js';
import { getToken, setToken, getPostAuthRedirect, clearPostAuthRedirect, getEmail, getRememberedEmail, clearSessionExpired } from '../auth-store.js';

export function renderUnlock(container) {
  if (hasKeys()) {
    const redirect = getPostAuthRedirect();
    clearPostAuthRedirect();
    navigate(redirect || '#/items');
    return;
  }

  container.innerHTML = `
    <div class="login-card">
      <div class="logo">
        <svg viewBox="0 0 32 32" width="48" height="48"><defs><linearGradient id="bk-unlock" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#3ecfb4"/><stop offset="100%" stop-color="#2a9d8f"/></linearGradient></defs><rect width="32" height="32" rx="7" fill="url(#bk-unlock)"/><rect x="10" y="14" width="12" height="10" rx="2" fill="#0c0e14"/><path d="M12.5 14V11a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="#0c0e14" stroke-width="2.2" stroke-linecap="round"/><circle cx="16" cy="19" r="1.5" fill="url(#bk-unlock)"/><line x1="16" y1="20.5" x2="16" y2="22" stroke="url(#bk-unlock)" stroke-width="1.5" stroke-linecap="round"/></svg>
        <div class="brand">Blind<span>Keep</span></div>
      </div>
      <div id="unlock-msg"></div>
      <div id="unlock-loading" style="text-align:center;color:var(--text-secondary);padding:1rem 0">Loading...</div>
      <div id="unlock-passkey-only" style="display:none">
        <p style="color:var(--text-secondary);margin-bottom:1.5rem;text-align:center">
          Your vault is secured with a passkey. Tap below to unlock.
        </p>
        <button id="passkey-unlock-btn" class="btn btn-primary" style="width:100%"><i data-lucide="fingerprint" style="width:18px;height:18px"></i> Unlock with Passkey</button>
        <p style="text-align:center;margin-top:16px;font-size:13px">
          <a href="#/recover" style="color:var(--text-secondary)">Can't use your passkey? Recover with your 12 words</a>
        </p>
      </div>
      <div id="unlock-password" style="display:none">
        <p style="color:var(--text-secondary);margin-bottom:1.5rem;text-align:center">
          Enter your password to decrypt your vault.
        </p>
        <form id="unlock-form">
          <div class="field"><label>Password</label><input type="password" name="password" required></div>
          <button type="submit" class="btn btn-primary" style="margin-top:8px">Unlock</button>
        </form>
        <p style="text-align:center;margin-top:16px;font-size:13px">
          <a href="#/recover" style="color:var(--text-secondary)">Forgot your passphrase? Recover with your 12 words</a>
        </p>
      </div>
    </div>
  `;
  lucideIcons();

  // Check for valid token before making API calls
  if (!getToken()) {
    document.getElementById('unlock-loading').style.display = 'none';
    document.getElementById('unlock-msg').innerHTML = '<div class="msg msg-error">Your session has expired. Please <a href="#/login" style="color:var(--accent)">sign in again</a>.</div>';
    return;
  }

  initUnlock();

  async function initUnlock() {
    try {
      const me = await get('/auth/me');
      document.getElementById('unlock-loading').style.display = 'none';

      if (me.has_password === false) {
        showPasskeyUnlock(me);
      } else {
        showPasswordUnlock(me);
      }
    } catch (err) {
      document.getElementById('unlock-loading').style.display = 'none';
      if (err.message === 'Session expired') {
        document.getElementById('unlock-msg').innerHTML = '<div class="msg msg-error">Your session has expired. Please <a href="#/login" style="color:var(--accent)">sign in again</a>.</div>';
      } else {
        document.getElementById('unlock-msg').innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
      }
    }
  }

  function showPasskeyUnlock(me) {
    document.getElementById('unlock-passkey-only').style.display = '';
    lucideIcons();

    const email = me.email || getEmail() || getRememberedEmail();

    document.getElementById('passkey-unlock-btn').onclick = async () => {
      const btn = document.getElementById('passkey-unlock-btn');
      const msg = document.getElementById('unlock-msg');
      btn.disabled = true;
      msg.innerHTML = '';

      try {
        const result = await authenticate(email);
        setToken(result.token);

        if (result.has_prf && result.prf_output && result.prf_wrapped_master_key) {
          unwrapAndStoreKeysFromPrf(
            result.prf_output,
            result.prf_wrapped_master_key,
            result.encrypted_private_key,
            result.public_key,
            result.encrypted_private_key_format_version,
          );
          if (isTauri) {
            const encKeyCopy = getEncKey();
            try { await window.__TAURI_INTERNALS__.invoke('set_enc_key', { encKey: Array.from(encKeyCopy) }); } catch (e) {} finally { wipe(encKeyCopy); }
          }
          const redirect = getPostAuthRedirect();
          clearPostAuthRedirect();
          navigate(redirect || '#/items');
        } else {
          msg.innerHTML = '<div class="msg msg-error">Passwordless unlock failed. Your authenticator may not support it. <a href="#/recover" style="color:var(--accent)">Recover with your 12 words</a>.</div>';
          btn.disabled = false;
        }
      } catch (err) {
        if (err.name === 'NotAllowedError') {
          msg.innerHTML = '<div class="msg msg-error">Passkey operation was cancelled.</div>';
        } else {
          msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
        }
        btn.disabled = false;
      }
    };
  }

  function showPasswordUnlock(me) {
    document.getElementById('unlock-password').style.display = '';

    document.getElementById('unlock-form').onsubmit = async (e) => {
      e.preventDefault();
      const msg = document.getElementById('unlock-msg');
      const btn = e.target.querySelector('button');
      const password = e.target.password.value;
      btn.disabled = true;
      msg.innerHTML = '';

      try {
        deriveAndStoreKeys(
          password,
          me.client_salt,
          me.encrypted_private_key,
          me.public_key,
          me.encrypted_master_key,
          me.encrypted_master_key_format_version,
          me.encrypted_private_key_format_version,
        );

        // If the current JWT is Limited (magic-link login), swap it for a
        // Full JWT now that the user has proven they hold the password. We
        // detect the tier via the `session_kind` claim in me's response.
        // /items etc. require Full — without this, the redirect below lands
        // on /account via requireAuth's isLimited() check.
        if (me.session_kind === 'limited') {
          const upgraded = await post('/auth/upgrade-session', { auth_key: getAuthKeyHex() });
          setToken(upgraded.token);
        }

        if (!me.encrypted_master_key) {
          msg.innerHTML = '<div class="msg msg-info">Upgrading vault security...</div>';
          const migration = migrateToMasterKey(password, me.client_salt);
          try {
            const items = await get('/items');
            const rewrapped = rewrapItemKeys(items, migration.oldEncKey, migration.newEncKey);
            await post('/auth/migrate-keys', {
              encrypted_master_key: migration.encryptedMasterKey,
              encrypted_private_key: migration.encryptedPrivateKey,
              items: rewrapped,
            });
            applyMigratedKeys(migration.masterKey, migration.newEncKey);
          } finally {
            // oldEncKey is a snapshot of the pre-migration encKey; wipe it once
            // the swap has happened so it doesn't linger on the heap. (#136)
            wipe(migration.oldEncKey);
          }
        } else if (needsParamUpgrade()) {
          msg.innerHTML = '<div class="msg msg-info">Upgrading encryption parameters...</div>';
          const upgraded = upgradeArgonParams(password, me.client_salt);
          await post('/auth/upgrade-params', upgraded);
        } else if (needsAadUpgrade()) {
          msg.innerHTML = '<div class="msg msg-info">Upgrading encryption format...</div>';
          const upgraded = upgradeArgonParams(password, me.client_salt);
          await post('/auth/upgrade-params', { ...upgraded, reason: 'aad_upgrade' });
        }

        const redirect = getPostAuthRedirect();
        clearPostAuthRedirect();
        navigate(redirect || '#/items');
      } catch (err) {
        clearKeys();
        if (err.message === 'Session expired') {
          clearSessionExpired();
          msg.innerHTML = '<div class="msg msg-error">Your session has expired. Please <a href="#/login" style="color:var(--accent)">sign in again</a>.</div>';
        } else {
          msg.innerHTML = `<div class="msg msg-error">Incorrect password. <a href="#/recover" style="color:var(--accent)">Forgot your password?</a></div>`;
        }
        btn.disabled = false;
      }
    };
  }
}
